import { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateFAQSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { getFAQs } from '@/lib/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight } from 'lucide-react';
import { trackBookCall } from '@/lib/gtm';

const defaultFAQs = [
  { id: '1', question: "What kind of agency is Wizbang?",
    answer: "We're a creative, media and technology studio. Six disciplines under one roof: performance marketing, e-commerce development, web development, custom CRM development, package designing and branding. One team, one P&L, one shared metric — your revenue." },
  { id: '2', question: "How long does it take to see results?",
    answer: "Performance work shows signal in 30-60 days; brand and product builds typically ship in 8-16 weeks. We refuse to confuse 'busy' with 'progress' — every sprint has a tangible deliverable." },
  { id: '3', question: "What industries do you work with?",
    answer: "We work best with D2C, e-commerce, SaaS, fintech, education and ambitious B2B service businesses. If your model has a clear customer and a clear price, we can engineer growth around it." },
  { id: '4', question: "How much do your engagements cost?",
    answer: "It depends on scope. Retainers usually start at ₹1.5L/month; project-based builds (websites, ERPs, CRMs, packaging systems) start at ₹3L. We share a one-page proposal in 48 hours of every brief." },
  { id: '5', question: "Do you work with founders directly?",
    answer: "Yes. Most of our engagements involve a founder, a CMO or an operator — the people who own the number. We talk to decision-makers, not approval chains." },
  { id: '6', question: "What's the Wizbang Intelligence Suite?",
    answer: "It's our in-house product line: WizbangEduOS (school operations), WizbangAdOS (ads analytics AI), WizbangSearchOS (SEO + auditing AI) and WizbangChatOS (WhatsApp platform). All currently in private beta." },
  { id: '7', question: "Can you work with our existing team?",
    answer: "Absolutely. We plug in next to in-house teams all the time — as an extension, a force multiplier or a finisher. We're allergic to gatekeeping." },
  { id: '8', question: "What does the first 30 days look like?",
    answer: "Week 1: discovery + access. Week 2: a written growth architecture. Week 3: first shipped work — usually a campaign, page or system module. Week 4: first measurement + iteration plan." },
];

export default function FAQ() {
  const [faqs, setFaqs] = useState(defaultFAQs);
  const [query, setQuery] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const res = await getFAQs();
      if (res.data?.length > 0) setFaqs(res.data);
    } catch (_) {}
  };

  const filtered = faqs.filter((f) => {
    const q = query.toLowerCase();
    return !q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="faq-page">
      <SEO
        title="FAQ — Working with Wizbang"
        description="Common questions about Wizbang's creative, media and technology services — pricing, process, timelines, industries we serve and what the first 30 days look like."
        keywords="wizbang faq, creative agency questions, marketing agency pricing India, agency engagement model, growth agency process"
        url="/faq"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            generateFAQSchema(faqs),
            generateBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }])
          ]
        }}
      />
      <Navbar />

      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">FAQ</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 max-w-5xl tracking-tight leading-[0.95]">
            Questions worth <span className="font-editorial text-[#ff5a1f]">honest</span> answers.
          </h1>
          <p className="text-[#3A3A3A] text-lg md:text-xl max-w-2xl leading-relaxed">
            Everything founders, CMOs and operators ask before working with us. Can't find what you need? Hit the contact page — we reply personally.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="border-y bg-white" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
            <input data-testid="faq-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-12 pr-4 py-3 border rounded-full text-sm text-[#0A0A0A] focus:outline-none focus:border-[#ff5a1f]"
              style={{ borderColor: 'var(--border)', background: '#FAFAF7' }} />
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {filtered.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-3">
              {filtered.map((faq, idx) => (
                <AccordionItem key={faq.id} value={faq.id} data-testid={`faq-item-${idx}`}
                  className="bg-white border px-6 rounded-xl data-[state=open]:border-[#ff5a1f] transition-colors"
                  style={{ borderColor: 'var(--border)' }}>
                  <AccordionTrigger className="text-left font-syne font-bold text-[#0A0A0A] hover:text-[#ff5a1f] py-5 hover:no-underline text-base md:text-lg">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5B5B5B] pb-5 leading-relaxed text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#5B5B5B] text-lg mb-4">No questions match "{query}".</p>
              <button onClick={() => setQuery('')} className="text-[#ff5a1f] font-semibold">Show all</button>
            </div>
          )}
        </div>
      </section>

      <section className="py-24 bg-white border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
            Still have a question?
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-10">We answer every brief personally. Promise.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-8 py-4 text-sm font-semibold rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center gap-2">
              Contact us
            </Link>
            <Link to="/book-appointment" onClick={() => trackBookCall('faq_bottom_cta')}
              className="bg-[#0A0A0A] text-white px-8 py-4 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
              Book a call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
